import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TransactionData, ISdkLogs } from './api-request';
import { TransactionFees } from './api-response';
import { AppService } from './app.service';
import { SelectedPaymentTokens, StringHelper } from './strings-helper';
import { postTransactionMessage } from './events';
import { environment } from 'src/environments/environment';
import { CommonModule } from '@angular/common';
import { SvgComponent } from './svg/svg.component';
import { CardChannelComponent } from './card-channel/card-channel.component';
import { TransferChannelComponent } from './transfer-channel/transfer-channel.component';
import { QRChannelComponent } from './qr-channel/qr-channel.component';
import { GooglePayComponent } from './google-pay/google-pay.component';
import { ApplePayChannelComponent } from './apple-pay-channel/apple-pay-channel.component';
import { PayattitudeChannelComponent } from './payattitude-channel/payattitude-channel.component';
import { DirectDebitChannelComponent } from './direct-debit-channel/direct-debit-channel.component';
import { UssdChannelComponent } from './ussd-channel/ussd-channel.component';
import { PaywithopayChannelComponent } from './paywithopay-channel/paywithopay-channel.component';

/**
 * Created by Stephen on 10/09/2022.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SvgComponent,
    CardChannelComponent,
    TransferChannelComponent,
    QRChannelComponent,
    GooglePayComponent,
    ApplePayChannelComponent,
    PayattitudeChannelComponent,
    DirectDebitChannelComponent,
    UssdChannelComponent,
    PaywithopayChannelComponent,
  ],
  animations: [
    trigger('slideMenuInOut', [
      state(
        'in',
        style({
          opacity: 0,
        }),
      ),
      state(
        'out',
        style({
          opacity: 1,
          zIndex: 100,
        }),
      ),
      transition('in => out', animate('400ms ease-in-out')),
      transition('out => in', animate('400ms ease-in-out')),
    ]),
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('400ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('400ms', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'omni-payment-gateway-sdk';
  client = 'zest';
  menuState: string = 'out';
  clientName: string = `${this.client} Payments Limited`;
  selectedChannelName: string = '';
  mobileView: 'payment' | 'channel' = 'channel';
  isMobile: boolean = false;
  private mediaQuery: MediaQueryList | null = null;
  isProd!: boolean;
  sdkLogs: ISdkLogs[] = [];
  ipAddress: string = '';

  state = {
    visible: false,
    isInitialized: false,
    completed: false,
    error: false,
  };

  errorMessage!: string;
  appLogo = `api.dev.gateway.com/assets/img/${this.client}-logo.png`;
  showLogo: boolean = false;
  activePaymentTab!: string;

  private setState(key: any, value: any) {
    this.state = { ...this.state, [key]: value };
    this.cd.detectChanges();
  }

  transactionData!: TransactionData;
  amount = 0;
  fee = 0;
  subtotal = 0;
  transactionFees!: TransactionFees;
  reference!: string;
  visitedChannels = new Set<string>();

  directDebit: boolean = false;
  visaQR: boolean = false;
  channels: SelectedPaymentTokens[] = [];
  optionalChannels: SelectedPaymentTokens[] = [];
  selectedChannels: SelectedPaymentTokens[] = [];
  currency: string = 'NGN';
  callback: any = {};

  showChannelsScrollArrow: boolean = false;
  @ViewChild('paymentChannelsList')
  paymentChannelsList!: ElementRef<HTMLElement>;

  constructor(
    private cd: ChangeDetectorRef,
    private el: ElementRef,
    public appService: AppService,
  ) {}

  ngOnInit(): void {
    window.addEventListener('message', this.handleEvent);
    this.initMobileDetection();
  }

  ngAfterViewInit(): void {
    this.checkPaymentChannelsScroll();
  }

  private checkPaymentChannelsScroll() {
    setTimeout(() => {
      this.showChannelsScrollArrow = StringHelper.hasScrollDown(
        this.paymentChannelsList?.nativeElement,
      );
    }, 0);
  }

  onPaymentChannelsScroll() {
    this.showChannelsScrollArrow = StringHelper.hasScrollDown(
      this.paymentChannelsList?.nativeElement,
    );
  }

  scrollPaymentChannelsDown() {
    StringHelper.scrollElementBy(this.paymentChannelsList?.nativeElement);
  }

  private initMobileDetection() {
    this.mediaQuery = window.matchMedia('(max-width: 767px)');
    this.isMobile = this.mediaQuery.matches;
    this.mediaQuery.addEventListener('change', this.onBreakpointChange);
  }

  private onBreakpointChange = (e: MediaQueryListEvent) => {
    this.isMobile = e.matches;
  };

  handleEvent = (event: any) => {
    const isNewTransactionEvent =
      typeof event.data === 'object' && event.data.type === 'omni:url';
    if (isNewTransactionEvent) {
      postTransactionMessage('loadedCheckout');

      if (event.data?.params) {
        this.transactionData = event.data.params;
        this.channels = event.data.params.channels;
        this.subtotal = StringHelper.toMajorAmount(event.data?.params?.amount);
        this.checkPaymentChannelsScroll();
        this.currency = event.data.params.currency;
        this.sdkLogs = event.data.params?.sdkHashTracker;
        const key = this.transactionData.key;
        this.isProd = key.includes('TPK_') ? false : environment.production;

        this.appService.transactionData = this.transactionData;
        this.appService.processData();
        this.setTransaction(event.data.params);
      }
    }
  };

  getIPAddress(params: any) {
    this.appService.getBrowserIP().subscribe({
      next: (res) => {
        this.ipAddress = res.ip;
        params = { ...params, ipAddress: this.ipAddress };
        this.sendSDKLogs(params);
      },
      error: (error) => console.error(error),
    });
  }

  sendSDKLogs(params: any) {
    this.appService.logSDKHash(params).subscribe({
      next: (res: any) => {},
      error: (error: any) => {},
    });
  }

  setTransaction(r: TransactionData) {
    let temp = new Object();
    let params = new Object();
    if (r?.paymentMode) {
      temp = {
        paymentMode: r.paymentMode,
        paymentLinkReference: r.paymentLinkReference,
      };
    }
    if (r?.metadata) {
      temp = { ...temp, metadata: r.metadata };
    }
    if (r?.ref) {
      temp = { ...temp, reference: r.ref };
    }
    const data = {
      email: r.email,
      amount: r.amount,
      currency: r.currency,
      binLookUp: true,
      ...temp,
    };

    if (Array.isArray(this.sdkLogs) && this.sdkLogs.length == 1) {
      params = {
        hash: this.sdkLogs[0]?.integrity,
        sdkUrl: this.sdkLogs[0]?.src,
        siteUrl: this.transactionData.metadata['referrer'],
      };
    }

    // remove this handler
    window.removeEventListener('message', this.handleEvent);
    this.appService.initializeTransaction(data).subscribe({
      next: (res: any) => {
        if (res.success) {
          Object.keys(params).length !== 0 && this.getIPAddress(params);
          // Get the selected payment method from the api and sort them in same order as design
          const sorted = this.appService.sortPaymentChannels(
            res.data.merchantSelectedTokens,
          );

          const availableChannels = res.data.paymentTokens;

          this.optionalChannels =
            this.appService.getDefaultChannelsFromFeatureFlags(
              availableChannels,
            );
          this.selectedChannels = this.appService.applyFeatureFlags(
            sorted,
            availableChannels,
          );

          this.channels = StringHelper.isEmptyArray(this.selectedChannels)
            ? this.optionalChannels
            : this.selectedChannels;
          this.checkPaymentChannelsScroll();

          this.reference = res.data.reference;
          this.transactionFees = new TransactionFees(res.data.transactionFees);
          this.amount = StringHelper.toMajorAmount(
            +this.transactionData.amount + this.transactionFees.WEB_CARD,
          );
          const socket = {
            txnRef: res.data.reference,
            txnData: r,
            savedCard: res.data.hasSavedCards,
            transactionFees: new TransactionFees(res.data.transactionFees),
            subtotal: this.subtotal,
          };

          this.appService.sendData(socket);
          this.setState('isInitialized', true);

          // Warm the card encryption key; a failure here isn't cached, so the
          // card submit path makes one fresh attempt of its own.
          this.appService.getEncryptionKey().catch(() => {});

          postTransactionMessage('loadedTransaction');

          this.setDefaultActiveChannel();
        }
      },
      error: (err: any) => {
        this.errorMessage = StringHelper.getErrorMessage(err.error);
        this.setState('error', true);
        postTransactionMessage('error', this.errorMessage);
      },
    });
  }

  selectChannel(channel: SelectedPaymentTokens) {
    const channelName = this.appService.modifyPaymentTokenName(
      channel.channelName,
    );

    this.activePaymentTab = channelName.toLowerCase().trim();
    this.selectedChannelName = channelName;
    this.visitedChannels.add(this.activePaymentTab);

    this.channels.forEach((c) => (c.isActive = c === channel));

    this.setTransactionAmount(this.activePaymentTab);
    if (this.isMobile) this.mobileView = 'payment';
  }

  hasVisitedChannel(channel: string): boolean {
    return this.visitedChannels.has(channel.toLowerCase());
  }

  setDefaultActiveChannel() {
    const activeTabElement = this.channels.find(
      (channel: SelectedPaymentTokens) => channel.isActive,
    );

    if (activeTabElement) {
      activeTabElement.isActive = true;
      this.activePaymentTab = activeTabElement.channelName.toLowerCase().trim();
      this.visitedChannels.add(this.activePaymentTab);

      this.setTransactionAmount(this.activePaymentTab);
    } else {
      this.channels[0].isActive = true;
      this.activePaymentTab = this.channels[0].channelName.toLowerCase().trim();
      this.visitedChannels.add(this.activePaymentTab);

      this.setTransactionAmount(this.activePaymentTab);
    }
  }

  initAccountsListener() {
    const accountsTab = document.querySelectorAll(
      'button[data-channel="account"]',
    );
    document.getElementById('transfer_channel')?.click();
    accountsTab.forEach((el) => {
      el.addEventListener('shown.bs.tab', (e) => {
        const activeAccountTab = (e.target as HTMLInputElement).id;
        if (activeAccountTab == 'transfer-tab') {
          document.getElementById('transfer_channel')?.click();
        } else {
          document.getElementById('direct-debit_channel')?.click();
        }
      });
    });
  }

  setTransactionAmount(activeTab: string) {
    switch (activeTab) {
      case 'card': {
        // A BIN lookup may already have confirmed an international fee for this
        // transaction; don't drop back to the domestic one on a tab switch.
        const savedFee = this.appService.getCardFeeState(this.reference);
        const feeMinor = savedFee?.international
          ? savedFee.feeMinor
          : this.transactionFees.WEB_CARD;
        this.amount = StringHelper.toMajorAmount(
          +this.transactionData.amount + feeMinor,
        );
        this.fee = StringHelper.toMajorAmount(feeMinor);

        break;
      }
      case 'transfer':
        this.amount = StringHelper.toMajorAmount(
          +this.transactionData.amount + this.transactionFees.WEB_TRANSFER,
        );
        this.fee = StringHelper.toMajorAmount(
          this.transactionFees.WEB_TRANSFER,
        );

        break;
      case 'qr':
        this.amount = StringHelper.toMajorAmount(
          +this.transactionData.amount + this.transactionFees.WEB_QR,
        );
        this.fee = StringHelper.toMajorAmount(this.transactionFees.WEB_QR);

        break;
      case 'ussd':
        this.amount = StringHelper.toMajorAmount(
          +this.transactionData.amount + this.transactionFees.WEB_USSD,
        );
        this.fee = StringHelper.toMajorAmount(this.transactionFees.WEB_USSD);

        break;
      case 'google pay':
      case 'apple pay':
        this.amount = StringHelper.toMajorAmount(
          +this.transactionData.amount + this.transactionFees.WEB_CARD,
        );
        this.fee = StringHelper.toMajorAmount(this.transactionFees.WEB_CARD);

        break;

      case 'payattitude':
        this.amount = StringHelper.toMajorAmount(
          +this.transactionData.amount + this.transactionFees.WEB_PAYATTITUDE,
        );
        this.fee = StringHelper.toMajorAmount(
          this.transactionFees.WEB_PAYATTITUDE,
        );

        break;

      case 'opay':
        this.amount = StringHelper.toMajorAmount(
          +this.transactionData.amount + this.transactionFees.WEB_OPAY,
        );
        this.fee = StringHelper.toMajorAmount(this.transactionFees.WEB_OPAY);

        break;

      default:
        this.amount = 0;
        this.fee = 0;
        break;
    }
  }

  onHarmBurgerClicked() {
    this.menuState = this.menuState === 'out' ? 'in' : 'out';
  }

  closePopup() {
    this.resetState();
    postTransactionMessage('close');
    window.addEventListener('message', this.handleEvent);
  }

  private resetState() {
    this.selectedChannelName = '';
    this.mobileView = 'channel';
    this.state = {
      visible: false,
      isInitialized: false,
      completed: false,
      error: false,
    };
    this.errorMessage = undefined!;
    this.showLogo = false;
    this.activePaymentTab = undefined!;
    this.amount = 0;
    this.transactionFees = undefined!;
    this.reference = undefined!;
    this.directDebit = false;
    this.visaQR = false;
    this.channels = [];
    this.optionalChannels = [];
    this.selectedChannels = [];
    this.showChannelsScrollArrow = false;
    this.currency = 'NGN';
    this.callback = {};
    this.menuState = 'out';
    this.transactionData = undefined!;
    this.appService.transactionData = undefined!;
    this.appService.clearVirtualAccountState();
    this.appService.clearUssdState();
    this.appService.clearCardFeeState();
    this.visitedChannels.clear();
  }

  changePaymentMethod() {
    this.mobileView = 'channel';
  }

  onRedirect() {
    this.menuState = 'in';
  }

  onCardFeeChange(e: { amount: number; fee: number }) {
    this.amount = e.amount;
    this.fee = e.fee;
  }

  paymentCompleted(callbackOptions: any) {
    this.callback = callbackOptions;
    this.setState('completed', true);
    setTimeout(() => {
      this.endTransaction();
    }, 3000);
  }

  endTransaction() {
    const response = {
      reference: this.reference,
      message: 'Approved',
      redirecturl: this.callback ? `${this.callback.url}` : null,
      status: 'success',
      metadata: this.transactionData.metadata,
    };
    postTransactionMessage('success', response);
    window.addEventListener('message', this.handleEvent);
    this.setState('completed', false);
    this.setState('error', false);

    // The popup can be reused for another transaction without being closed, so
    // per-transaction state has to go once this one is done.
    this.appService.clearVirtualAccountState();
    this.appService.clearUssdState();
    this.appService.clearCardFeeState();
  }

  reInitialize() {
    this.setState('error', false);
    this.setTransaction(this.transactionData);
  }

  checkChannels(channel: string): boolean {
    channel = channel.toUpperCase();

    if (this.currency === 'USD') {
      return channel === 'CARD';
    }

    if (!this.channels || this.channels.length === 0) {
      return true;
    }

    return this.channels.some(
      (item) =>
        item.isActive &&
        item.channelName.toUpperCase() === channel.toUpperCase(),
    );
  }

  checkCurrency(): boolean {
    if (this.currency && this.currency == 'USD') {
      // this.menuState = 'in';
      this.activePaymentTab = 'card';
      this.selectedChannelName = 'card';
      this.mobileView = 'payment';
      this.setTransactionAmount('card');
      return false;
    }
    return true;
  }

  goBack() {
    this.mobileView = 'channel';
  }

  ngOnDestroy(): void {
    // Remove the event listener when the component is destroyed
    window.removeEventListener('message', this.handleEvent);
    this.mediaQuery?.removeEventListener('change', this.onBreakpointChange);
  }
}
